import { Link } from "lucide-react";
import { FC } from "react";
import { useGetInventories } from "~/services/inventory";
import { Spin, Empty } from "antd";

type Props = {
  value?: string[];
  onChange?: (value: string[]) => void;
  clientId?: string;
};

export const InventoriesSelect: FC<Props> = ({ value, onChange, clientId }) => {
  const { data: inventoryItems, isLoading } = useGetInventories(
    clientId ? { clientId, pageSize: 100 } : undefined,
    { enabled: !!clientId }
  );

  const handleRelatedItemChange = (id: string) => {
    if (value?.includes(id)) {
      onChange?.(value.filter((itemId) => itemId !== id));
    } else {
      onChange?.([...(value || []), id]);
    }
  };

  if (!clientId) {
    return (
      <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg border border-dashed">
        Select a client first to link inventory items.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center p-4">
        <Spin size="small" />
        <span className="ml-2 text-sm text-gray-500">Loading inventory...</span>
      </div>
    );
  }

  if (!inventoryItems?.items?.length) {
    return (
      <Empty
        description="No inventory items found for this client"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
      {inventoryItems.items.map((item) => (
        <label
          key={item.id}
          className={`flex items-center space-x-2 p-2 border rounded-lg cursor-pointer ${
            value?.includes(item.id) ? "bg-blue-50 border-blue-300" : ""
          }`}
          onClick={() => handleRelatedItemChange(item.id)}
        >
          <Link
            size={16}
            className={
              value?.includes(item.id) ? "text-blue-500" : "text-gray-400"
            }
          />
          <span className="mr-1 font-medium">{item.inventoryRef}:</span>
          <span>{item.title}</span>
        </label>
      ))}
    </div>
  );
};
